// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';

/**
 * Imagery-analysis state for one course, for the admin panel on the course page.
 *
 * Separate from useCourseAnalysis, which lists the features themselves. This one answers the
 * question that comes before that: has analysis run at all, and if not, whose move is it.
 */

/** Stored features of one kind from one source. */
export type AnalysisFeatureBreakdown = {
  kind: string;
  source: string;
  count: number;
  /**
   * How many carry boundary geometry. The number that matters: a course can hold tee and
   * green rows from OSM and still have nothing to draw, because those are centre points.
   */
  withPolygon: number;
  reviewed: number;
  /** Rows a person rejected: hidden from players, kept as training labels. */
  rejected: number;
};

/** What one imagery provider (or model) produced in one run. Typed, not prose. */
export type AnalysisProviderResult = {
  provider: string;
  providerVersion: string | null;
  zoom: number;
  tilesFetched: number;
  tilesAttempted: number;
  partial: boolean;
  error: string | null;
  /** Raw candidate counts per kind, before publication. */
  counts: Record<string, number>;
  written: number;
  replaced: number;
  skippedStrongerSource: number;
  skippedUnsupportedKind: number;
  /** Agreement with the human-accepted features; null when nothing was accepted to compare with. */
  precisionVsAccepted: number | null;
  recallVsAccepted: number | null;
};

/** One analysis pass over the course. */
export type AnalysisRun = {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  /**
   * awaiting-data and dispatched are the worker-mode waits: prepared but waiting for the
   * course's imagery, and handed to a batch worker that has not answered yet. Both can
   * legitimately last for hours — the fleet may be switched off — so neither is a failure.
   */
  status: 'awaiting-data' | 'dispatched' | 'running' | 'succeeded' | 'partial' | 'failed';
  error: string | null;
  durationSeconds: number | null;
  radiusMetres: number;
  zoom: number;
  anchorCount: number;
  /** classical | model — which detector drew this pass's shapes. */
  detector: string;
  /**
   * Which model this pass was asked for, when the fleet had more than one. Null on a
   * classical pass, and on a model pass that took whichever checkpoint fitted the imagery.
   * A course is followed by one comparison run per model, so this is what tells them apart.
   */
  modelVersion: string | null;
  /**
   * The thread every message and log line of this run carries — what a Kibana link filters on
   * to show one analysis across all four services. Null for a run that started before the id
   * began being stored.
   */
  correlationId?: string | null;
  /**
   * Set when this pass was the automatic model comparison that followed the run with this
   * id, rather than one anybody asked for. It publishes nothing — so a second run against
   * a course in the list is expected, not a sign of something rerunning.
   */
  followUpOfRunId: string | null;
  providers: AnalysisProviderResult[];
  /**
   * The jobs this run fanned out into — one per provider×detector, later the side products —
   * each with its own status, worker and timing. What a run is waiting for, and on whom, is
   * read off these rather than off the run.
   */
  jobs: AnalysisJob[];
};

/** One job of a run: one provider×detector, or one side product. */
export type AnalysisJob = {
  id: string;
  /** detection | canopy-height | elevation | imagery-products. */
  kind: string;
  provider: string | null;
  /** classical | model, for a detection. */
  detector: string | null;
  modelVersion: string | null;
  status: 'planned' | 'dispatched' | 'succeeded' | 'partial' | 'failed';
  storedOnly: boolean;
  /** While planned: the source whose acquisition this job is waiting for. */
  waitingOn: string | null;
  requestedAt: string | null;
  dispatchedAt: string | null;
  finishedAt: string | null;
  /** Whole seconds from dispatch to answer — queue wait included. */
  durationSeconds: number | null;
  /** Whole seconds the worker itself spent. */
  workerSeconds: number | null;
  workerId: string | null;
  error: string | null;
};

/** A stored course picture with its provenance. */
export type CourseImage = CourseBasemapImage & {
  provider: string;
  kind: 'basemap' | 'nir' | 'hole' | string;
  holeNumber: number;
  zoom: number | null;
  imageryFetchedAt: string | null;
  createdAt: string | null;
};

/** The OpenStreetMap element the course was imported from, when known. */
export type CourseOsmIdentity = { osmType: string; osmId: number; refreshedAt: string | null };

/**
 * Where a course stands.
 *
 * - `disabled` — no analyser configured in this environment. Nothing else on the page matters.
 * - `neverRequested` — imported before analysis existed, or never imported from OSM. Re-run.
 * - `pending` — queued and waiting for the background loop.
 * - `abandoned` — failed its attempts and will not be retried without a nudge.
 * - `analysed` — a pass completed; the outcome says what it found.
 */
export type AnalysisState =
  | 'disabled'
  | 'neverRequested'
  | 'pending'
  | 'abandoned'
  | 'analysed';

/**
 * A stored basemap picture and the corners it covers. `url` is already a ready-to-use public
 * URL, not a bucket key — geodata-service builds it, this hook only carries it through.
 */
export type CourseBasemapImage = {
  url: string;
  north: number;
  west: number;
  south: number;
  east: number;
  /** Which provider captured the pixels; null on older payloads. */
  provider?: string | null;
  imageryFetchedAt?: string | null;
};

export type CourseAnalysisStatus = {
  courseId: string;
  courseName: string;
  enabled: boolean;
  state: AnalysisState;
  requestedAt: string | null;
  analysedAt: string | null;
  attempts: number;
  maxAttempts: number;
  features: AnalysisFeatureBreakdown[];
  /** The latest runs, newest first. Empty until one has run. */
  runs: AnalysisRun[];
  /** The newest stored picture per provider, kind and hole. */
  images: CourseImage[];
  /**
   * The whole-course basemap to draw under the shapes: the newest `basemap` picture of the
   * first provider that has one. Null whenever nothing is stored.
   */
  basemapImage: CourseBasemapImage | null;
  osm: CourseOsmIdentity | null;
  /**
   * Messages waiting or in flight on the estate-wide analysis queue, right now — not
   * per-course. Null when analysis is disabled or the depth could not be read.
   */
  queueDepth: number | null;
  /**
   * The detectors that can be asked for right now: always `classical`, plus `model` when a
   * batch worker able to run it is online. Empty on a backend that predates the choice, which
   * callers read as "classical only" rather than as "nothing works".
   */
  detectors: string[];
  /**
   * The job kinds a run can be asked for right now — detection always, the rest when a worker
   * that can serve them is online. Empty on a backend that predates the picker, which callers
   * read as "detection only".
   */
  jobKinds: string[];
  /**
   * The imagery providers a run can be asked for — what the planner would plan: the online
   * fleet's own imagery that covers the course, or the trust list when no worker is online.
   */
  providers: string[];
  /**
   * Trusted providers that cover the course but no online worker serves: not askable, shown
   * greyed so the admin reads why a provider is missing. Empty on a backend that predates it.
   */
  unavailableProviders: string[];
};

type Row = Record<string, unknown>;

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const STATES: AnalysisState[] = [
  'disabled',
  'neverRequested',
  'pending',
  'abandoned',
  'analysed',
];

const numOrNull = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const mapBasemapImage = (row: Row | null | undefined): CourseBasemapImage | null => {
  if (!row) return null;
  const url = str(row, 'url', 'Url');
  const north = numOrNull(row, 'north', 'North');
  const west = numOrNull(row, 'west', 'West');
  const south = numOrNull(row, 'south', 'South');
  const east = numOrNull(row, 'east', 'East');
  if (!url || north === null || west === null || south === null || east === null) return null;
  return {
    url,
    north,
    west,
    south,
    east,
    provider: str(row, 'provider', 'Provider'),
    imageryFetchedAt: str(row, 'imageryFetchedAt', 'ImageryFetchedAt'),
  };
};

const mapCourseImage = (row: Row): CourseImage | null => {
  const base = mapBasemapImage(row);
  if (!base) return null;
  return {
    ...base,
    provider: str(row, 'provider', 'Provider') ?? '',
    kind: str(row, 'kind', 'Kind') ?? '',
    holeNumber: num(row, 'holeNumber', 'HoleNumber'),
    zoom: numOrNull(row, 'zoom', 'Zoom'),
    imageryFetchedAt: str(row, 'imageryFetchedAt', 'ImageryFetchedAt'),
    createdAt: str(row, 'createdAt', 'CreatedAt'),
  };
};

const RUN_STATUSES: AnalysisRun['status'][] = [
  'awaiting-data',
  'dispatched',
  'running',
  'succeeded',
  'partial',
  'failed',
];

const mapProviderResult = (row: Row): AnalysisProviderResult => {
  const rawCounts = (row.counts ?? row.Counts) as Record<string, unknown> | undefined;
  const counts: Record<string, number> = {};
  if (rawCounts && typeof rawCounts === 'object') {
    for (const [key, value] of Object.entries(rawCounts)) {
      if (typeof value === 'number' && Number.isFinite(value)) counts[key] = value;
    }
  }
  return {
    provider: str(row, 'provider', 'Provider') ?? '',
    providerVersion: str(row, 'providerVersion', 'ProviderVersion'),
    zoom: num(row, 'zoom', 'Zoom'),
    tilesFetched: num(row, 'tilesFetched', 'TilesFetched'),
    tilesAttempted: num(row, 'tilesAttempted', 'TilesAttempted'),
    partial: row.partial === true || row.Partial === true,
    error: str(row, 'error', 'Error'),
    counts,
    written: num(row, 'written', 'Written'),
    replaced: num(row, 'replaced', 'Replaced'),
    skippedStrongerSource: num(row, 'skippedStrongerSource', 'SkippedStrongerSource'),
    skippedUnsupportedKind: num(row, 'skippedUnsupportedKind', 'SkippedUnsupportedKind'),
    precisionVsAccepted: numOrNull(row, 'precisionVsAccepted', 'PrecisionVsAccepted'),
    recallVsAccepted: numOrNull(row, 'recallVsAccepted', 'RecallVsAccepted'),
  };
};

const JOB_STATUSES: AnalysisJob['status'][] = ['planned', 'dispatched', 'succeeded', 'partial', 'failed'];

const mapJob = (row: Row): AnalysisJob | null => {
  const id = str(row, 'id', 'Id');
  const kind = str(row, 'kind', 'Kind');
  if (!id || !kind) return null;
  const rawStatus = str(row, 'status', 'Status');
  return {
    id,
    kind,
    provider: str(row, 'provider', 'Provider'),
    detector: str(row, 'detector', 'Detector'),
    modelVersion: str(row, 'modelVersion', 'ModelVersion'),
    // An unknown status is a job this frontend predates, not a failure; it reads as waiting.
    status: JOB_STATUSES.includes(rawStatus as AnalysisJob['status'])
      ? (rawStatus as AnalysisJob['status'])
      : 'dispatched',
    storedOnly: row.storedOnly === true || row.StoredOnly === true,
    waitingOn: str(row, 'waitingOn', 'WaitingOn'),
    requestedAt: str(row, 'requestedAt', 'RequestedAt'),
    dispatchedAt: str(row, 'dispatchedAt', 'DispatchedAt'),
    finishedAt: str(row, 'finishedAt', 'FinishedAt'),
    durationSeconds: numOrNull(row, 'durationSeconds', 'DurationSeconds'),
    workerSeconds: numOrNull(row, 'workerSeconds', 'WorkerSeconds'),
    workerId: str(row, 'workerId', 'WorkerId'),
    error: str(row, 'error', 'Error'),
  };
};

const mapRun = (row: Row): AnalysisRun | null => {
  const id = str(row, 'id', 'Id');
  const startedAt = str(row, 'startedAt', 'StartedAt');
  if (!id || !startedAt) return null;
  const rawStatus = str(row, 'status', 'Status');
  const rawProviders = row.providers ?? row.Providers;
  const rawJobs = row.jobs ?? row.Jobs;
  return {
    id,
    startedAt,
    finishedAt: str(row, 'finishedAt', 'FinishedAt'),
    status: RUN_STATUSES.includes(rawStatus as AnalysisRun['status'])
      ? (rawStatus as AnalysisRun['status'])
      : 'failed',
    error: str(row, 'error', 'Error'),
    durationSeconds: numOrNull(row, 'durationSeconds', 'DurationSeconds'),
    radiusMetres: num(row, 'radiusMetres', 'RadiusMetres'),
    zoom: num(row, 'zoom', 'Zoom'),
    anchorCount: num(row, 'anchorCount', 'AnchorCount'),
    detector: str(row, 'detector', 'Detector') ?? 'classical',
    modelVersion: str(row, 'modelVersion', 'ModelVersion'),
    correlationId: str(row, 'correlationId', 'CorrelationId'),
    followUpOfRunId: str(row, 'followUpOfRunId', 'FollowUpOfRunId'),
    providers: (Array.isArray(rawProviders) ? (rawProviders as Row[]) : []).map(mapProviderResult),
    jobs: (Array.isArray(rawJobs) ? (rawJobs as Row[]) : [])
      .map(mapJob)
      .filter((job): job is AnalysisJob => job !== null),
  };
};

const mapOsm = (row: Row | null | undefined): CourseOsmIdentity | null => {
  if (!row) return null;
  const osmType = str(row, 'osmType', 'OsmType');
  const osmId = numOrNull(row, 'osmId', 'OsmId');
  if (!osmType || osmId === null) return null;
  return { osmType, osmId, refreshedAt: str(row, 'refreshedAt', 'RefreshedAt') };
};

/** Payload casing varies across the services this passes through, so read both. */
export const mapAnalysisStatus = (row: Row): CourseAnalysisStatus | null => {
  const courseId = str(row, 'courseId', 'CourseId');
  if (!courseId) return null;

  const rawState = str(row, 'state', 'State');
  const rawFeatures = row.features ?? row.Features;
  const rawRuns = row.runs ?? row.Runs;
  const rawImages = row.images ?? row.Images;
  const runs = (Array.isArray(rawRuns) ? (rawRuns as Row[]) : [])
    .map(mapRun)
    .filter((run): run is AnalysisRun => run !== null);
  // Images arrive ordered by provider; the server's provider ranking is what puts the
  // preferred one first, so "the first basemap" is the one to draw.
  const images = (Array.isArray(rawImages) ? (rawImages as Row[]) : [])
    .map(mapCourseImage)
    .filter((image): image is CourseImage => image !== null);

  return {
    courseId,
    courseName: str(row, 'courseName', 'CourseName') ?? '',
    enabled: row.enabled === true || row.Enabled === true,
    // An unknown state is treated as pending rather than dropped: a newer service reporting
    // a state this build has never heard of should not blank the whole panel.
    state: STATES.includes(rawState as AnalysisState) ? (rawState as AnalysisState) : 'pending',
    requestedAt: str(row, 'requestedAt', 'RequestedAt'),
    analysedAt: str(row, 'analysedAt', 'AnalysedAt'),
    attempts: num(row, 'attempts', 'Attempts'),
    maxAttempts: num(row, 'maxAttempts', 'MaxAttempts'),
    features: (Array.isArray(rawFeatures) ? (rawFeatures as Row[]) : [])
      .map((feature) => ({
        kind: str(feature, 'kind', 'Kind') ?? '',
        source: str(feature, 'source', 'Source') ?? '',
        count: num(feature, 'count', 'Count'),
        withPolygon: num(feature, 'withPolygon', 'WithPolygon'),
        reviewed: num(feature, 'reviewed', 'Reviewed'),
        rejected: num(feature, 'rejected', 'Rejected'),
      }))
      .filter((feature) => feature.kind.length > 0),
    runs,
    images,
    basemapImage: images.find((image) => image.kind === 'basemap') ?? null,
    osm: mapOsm((row.osm ?? row.Osm) as Row | null | undefined),
    queueDepth: numOrNull(row, 'queueDepth', 'QueueDepth'),
    detectors: strings(row.detectors ?? row.Detectors),
    jobKinds: strings(row.jobKinds ?? row.JobKinds),
    providers: strings(row.providers ?? row.Providers),
    unavailableProviders: strings(row.unavailableProviders ?? row.UnavailableProviders),
  };
};

/** The strings in a payload array, dropping anything that is not one. */
const strings = (value: unknown): string[] =>
  ((value as unknown[] | undefined) ?? []).filter(
    (item): item is string => typeof item === 'string' && item.length > 0
  );

/**
 * Why the status could not be read.
 *
 * `unavailable` is the one worth telling apart: a 404 from this route means the backend
 * predates the endpoint, which is a deploy that has not happened rather than anything wrong
 * with the course. Reporting it as a generic failure sends the reader looking at the course.
 */
export type StatusError = 'forbidden' | 'unavailable' | 'failed';

export type CourseAnalysisStatusState = {
  status: CourseAnalysisStatus | null;
  loading: boolean;
  error: StatusError | null;
  refresh: () => void;
};

export function useCourseAnalysisStatus(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  /** Held off until the admin opens the panel; nobody else should spend the request. */
  enabled: boolean
): CourseAnalysisStatusState {
  const [status, setStatus] = useState<CourseAnalysisStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<StatusError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!enabled || !token || !courseId) {
      setStatus(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/analysis-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;

        if (!response.ok) {
          setStatus(null);
          // 404 here is the route itself missing, not the course: the BFF answers 404 for an
          // unknown course too, but a backend without this endpoint returns the same status,
          // and of the two the deploy is overwhelmingly the likelier and the more actionable.
          setError(
            response.status === 403
              ? 'forbidden'
              : response.status === 404
                ? 'unavailable'
                : 'failed'
          );
          return;
        }

        setStatus(mapAnalysisStatus((await response.json()) as Row));
      } catch {
        if (!cancelled) {
          setStatus(null);
          setError('failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, enabled, token, reloadKey]);

  return { status, loading, error, refresh };
}

/**
 * Which of a round's jobs to plan. An omitted or empty list means every one of that sort, so
 * an empty selection is the whole round — the same request the automatic sweep makes.
 */
export type AnalysisSelection = {
  /** detection | canopy-height | elevation | imagery-products. */
  kinds?: string[];
  /** classical | model. Only narrows the detection jobs. */
  detectors?: string[];
  /** Imagery provider names. Only narrows the jobs an imagery source produces. */
  providers?: string[];
};

/**
 * Queues the course for analysis.
 *
 * 'unavailable' is not a failure: it is the service saying this environment has no analyser
 * configured, which is the answer rather than an error, and the panel says so.
 */
export async function requestCourseAnalysis(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  /**
   * Which of the round's jobs to run. Omitted — every caller that just wants the course
   * analysed — means the whole round, the same thing the automatic sweep asks for.
   */
  selection?: AnalysisSelection
): Promise<'queued' | 'unavailable' | 'failed'> {
  if (!token || !courseId) return 'failed';
  try {
    const response = await fetch(
      `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/analyze`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ selection: selection ?? null }),
      }
    );
    if (response.ok) return 'queued';
    return response.status === 503 ? 'unavailable' : 'failed';
  } catch {
    return 'failed';
  }
}

export type HoleBasemapImageState = {
  image: CourseBasemapImage | null;
  loading: boolean;
  error: StatusError | null;
};

/**
 * One hole's cropped basemap picture -- course-analysis-service's per-hole counterpart to
 * the whole-course `basemapImage` above, small enough for the player hole view to draw
 * instead of shipping the whole course's picture just to look at one hole. The server picks
 * its preferred provider unless `provider` names one. `image` is null both while nothing has been analysed yet and once it has been but
 * found nothing for this specific hole -- there is nothing for a caller to tell those two
 * apart on, and nothing it needs to.
 */
export function useHoleBasemapImage(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  holeNumber: number | null,
  enabled: boolean,
  provider?: string | null
): HoleBasemapImageState {
  const [image, setImage] = useState<CourseBasemapImage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<StatusError | null>(null);

  useEffect(() => {
    if (!enabled || !token || !courseId || holeNumber === null) {
      setImage(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/holes/${holeNumber}/basemap-image${
            provider ? `?provider=${encodeURIComponent(provider)}` : ''
          }`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;

        if (!response.ok) {
          setImage(null);
          setError(
            response.status === 403
              ? 'forbidden'
              : response.status === 404
                ? 'unavailable'
                : 'failed'
          );
          return;
        }

        const body = (await response.json()) as Row | null;
        setImage(body ? mapBasemapImage(body) : null);
      } catch {
        if (!cancelled) {
          setImage(null);
          setError('failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, enabled, holeNumber, token, provider]);

  return { image, loading, error };
}
